import type { allLegalDocuments } from "content-collections";

export type LegalDocumentVersion = (typeof allLegalDocuments)[number];

export function formatLegalVersionDate(date: string | Date): string {
	return new Date(date).toLocaleDateString("en-US", {
		month: "long",
		day: "numeric",
		year: "numeric",
	});
}

export function getLegalVersionLabel(document: LegalDocumentVersion): string {
	return document.versionLabel ?? formatLegalVersionDate(document.lastUpdated);
}

export function listVersionsForDocument(
	documentId: string,
	documents: readonly LegalDocumentVersion[],
): LegalDocumentVersion[] {
	return [...documents]
		.filter((document) => document.documentId === documentId)
		.sort(
			(a, b) =>
				new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime(),
		);
}

export function getCurrentLegalVersion(
	documentId: string,
	documents: readonly LegalDocumentVersion[],
): LegalDocumentVersion | undefined {
	const versions = listVersionsForDocument(documentId, documents);
	return versions.find((document) => document.isCurrent) ?? versions[0];
}

export function getLegalVersion(
	documentId: string,
	versionId: string,
	documents: readonly LegalDocumentVersion[],
): LegalDocumentVersion | undefined {
	return documents.find(
		(document) =>
			document.documentId === documentId && document.versionId === versionId,
	);
}

/** One card per policy family (current version metadata). */
export function listLegalDocumentsForIndex(
	documents: readonly LegalDocumentVersion[],
): LegalDocumentVersion[] {
	const seen = new Map<string, LegalDocumentVersion>();
	for (const document of documents) {
		const current = getCurrentLegalVersion(document.documentId, documents);
		if (current) {
			seen.set(document.documentId, current);
		}
	}
	return [...seen.values()].sort((a, b) => a.order - b.order);
}
